"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link2,
  FileText,
  Circle,
  LayoutList,
  ExternalLink,
  Search,
  Loader2,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { STATUSES } from "@/lib/constants";
import { formatRelativeTime } from "@/lib/timezone";
import type { StatusId } from "@/lib/types";

/**
 * Shared management view — workspace-wide list of everything that has been
 * made public: docs, issues, and saved board views. Each row links to its
 * public URL (/p/<type>/<id> or /v/<token>), shows when it was shared, and
 * exposes a revoke action.
 *
 * Reads from GET /api/workspaces/<id>/shared. Revoke goes to
 * POST /api/share/<type>/<id> {is_public:false} (docs/issues) or
 * DELETE /api/public-views/<token> (views).
 */

interface SharedIssue {
  id: string;
  identifier: string;
  title: string;
  status: StatusId;
  made_public_at: string | null;
}
interface SharedDoc {
  id: string;
  title: string;
  icon: string;
  made_public_at: string | null;
}
interface SharedViewRow {
  token: string;
  name: string;
  entity_type: "issue" | "doc";
  created_at: string;
}
interface SharedPayload {
  issues: SharedIssue[];
  docs: SharedDoc[];
  views: SharedViewRow[];
}

type TypeFilter = "all" | "doc" | "issue" | "view";

const STATUS_META = new Map(STATUSES.map((s) => [s.id, s]));

export function SharedView({ canWrite }: { canWrite: boolean }) {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);

  const [data, setData] = useState<SharedPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusId | "">("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/shared`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `request failed (${res.status})`);
      }
      const json = (await res.json()) as SharedPayload;
      setData({
        issues: json.issues ?? [],
        docs: json.docs ?? [],
        views: json.views ?? [],
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load shared items");
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const revokeEntity = useCallback(
    async (type: "issue" | "doc", id: string) => {
      if (!canWrite) return;
      const key = `${type}:${id}`;
      setRevoking(key);
      try {
        const res = await fetch(`/api/share/${type}/${id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_public: false }),
        });
        if (!res.ok) throw new Error("revoke failed");
        setData((prev) =>
          prev
            ? {
                ...prev,
                issues:
                  type === "issue"
                    ? prev.issues.filter((i) => i.id !== id)
                    : prev.issues,
                docs:
                  type === "doc" ? prev.docs.filter((d) => d.id !== id) : prev.docs,
              }
            : prev
        );
      } catch {
        // Re-sync from the server so the UI doesn't drift on failure.
        void load();
      } finally {
        setRevoking(null);
      }
    },
    [canWrite, load]
  );

  const revokeView = useCallback(
    async (token: string) => {
      if (!canWrite) return;
      const key = `view:${token}`;
      setRevoking(key);
      try {
        const res = await fetch(`/api/public-views/${token}`, { method: "DELETE" });
        if (!res.ok) throw new Error("revoke failed");
        setData((prev) =>
          prev ? { ...prev, views: prev.views.filter((v) => v.token !== token) } : prev
        );
      } catch {
        void load();
      } finally {
        setRevoking(null);
      }
    },
    [canWrite, load]
  );

  const q = query.trim().toLowerCase();

  const visibleDocs = useMemo(() => {
    if (!data) return [];
    if (typeFilter !== "all" && typeFilter !== "doc") return [];
    return data.docs.filter((d) => !q || d.title.toLowerCase().includes(q));
  }, [data, typeFilter, q]);

  const visibleIssues = useMemo(() => {
    if (!data) return [];
    if (typeFilter !== "all" && typeFilter !== "issue") return [];
    return data.issues.filter(
      (i) =>
        (!statusFilter || i.status === statusFilter) &&
        (!q ||
          i.title.toLowerCase().includes(q) ||
          i.identifier.toLowerCase().includes(q))
    );
  }, [data, typeFilter, statusFilter, q]);

  const visibleViews = useMemo(() => {
    if (!data) return [];
    if (typeFilter !== "all" && typeFilter !== "view") return [];
    return data.views.filter((v) => !q || v.name.toLowerCase().includes(q));
  }, [data, typeFilter, q]);

  const totalShared =
    (data?.docs.length ?? 0) + (data?.issues.length ?? 0) + (data?.views.length ?? 0);
  const totalVisible = visibleDocs.length + visibleIssues.length + visibleViews.length;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Header + filters */}
      <div className="px-6 py-3 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
        <Link2 size={16} className="text-[var(--accent)]" />
        <h2 className="text-[14px] font-semibold">Shared</h2>
        <span className="text-[11.5px] text-[var(--fg-tertiary)]">·</span>
        <span className="text-[12px] text-[var(--fg-secondary)]">
          {totalShared} public item{totalShared === 1 ? "" : "s"}
        </span>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={11}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--fg-tertiary)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shared items…"
              className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md pl-7 pr-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)] w-[200px]"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)]"
          >
            <option value="all">All types</option>
            <option value="doc">Docs</option>
            <option value="issue">Tasks</option>
            <option value="view">Board views</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusId | "")}
            disabled={typeFilter === "doc" || typeFilter === "view"}
            className="bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1 text-[11.5px] outline-none focus:border-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-full text-[var(--fg-muted)] gap-2">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-[12.5px]">Loading shared items…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 text-[var(--fg-muted)]">
            <Link2 size={32} className="mb-3 opacity-40" />
            <div className="text-[13px] font-medium mb-1 text-[var(--red)]">
              Couldn&apos;t load shared items
            </div>
            <div className="text-[11.5px] max-w-md mb-3">{error}</div>
            <button
              onClick={() => void load()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--accent-bg)]/30 transition-colors text-[var(--fg-secondary)] text-[12px] font-medium"
            >
              Retry
            </button>
          </div>
        ) : totalShared === 0 ? (
          <EmptyState
            title="Nothing shared yet"
            body="Make a doc or task public from its detail view, or save a board view as a public link. Anything you share will show up here so you can keep track of what's exposed — and revoke it in one click."
          />
        ) : totalVisible === 0 ? (
          <EmptyState
            title="No matches"
            body="No shared items match the current filters."
          />
        ) : (
          <div className="px-6 py-4 space-y-7 max-w-3xl">
            {visibleDocs.length > 0 && (
              <Section icon={<FileText size={12} />} title="Docs" count={visibleDocs.length}>
                {visibleDocs.map((d) => (
                  <SharedRow
                    key={d.id}
                    href={`/p/doc/${d.id}`}
                    leading={<span className="text-[14px] leading-none">{d.icon || "📄"}</span>}
                    title={d.title || "Untitled doc"}
                    sharedAt={d.made_public_at}
                    revoking={revoking === `doc:${d.id}`}
                    onRevoke={canWrite ? () => void revokeEntity("doc", d.id) : undefined}
                  />
                ))}
              </Section>
            )}

            {visibleIssues.length > 0 && (
              <Section icon={<Circle size={12} />} title="Tasks" count={visibleIssues.length}>
                {visibleIssues.map((i) => {
                  const meta = STATUS_META.get(i.status);
                  return (
                    <SharedRow
                      key={i.id}
                      href={`/p/issue/${i.id}`}
                      leading={
                        <span className="font-mono text-[11px] text-[var(--fg-tertiary)] shrink-0">
                          {i.identifier}
                        </span>
                      }
                      title={i.title}
                      badge={
                        meta ? (
                          <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--fg-tertiary)]">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ background: meta.color }}
                            />
                            {meta.name}
                          </span>
                        ) : undefined
                      }
                      sharedAt={i.made_public_at}
                      revoking={revoking === `issue:${i.id}`}
                      onRevoke={canWrite ? () => void revokeEntity("issue", i.id) : undefined}
                    />
                  );
                })}
              </Section>
            )}

            {visibleViews.length > 0 && (
              <Section
                icon={<LayoutList size={12} />}
                title="Board views"
                count={visibleViews.length}
              >
                {visibleViews.map((v) => (
                  <SharedRow
                    key={v.token}
                    href={`/v/${v.token}`}
                    leading={
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--fg-tertiary)] shrink-0">
                        {v.entity_type === "doc" ? <FileText size={11} /> : <Circle size={11} />}
                        {v.entity_type === "doc" ? "Docs" : "Tasks"}
                      </span>
                    }
                    title={v.name}
                    sharedAt={v.created_at}
                    revoking={revoking === `view:${v.token}`}
                    onRevoke={canWrite ? () => void revokeView(v.token) : undefined}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  count,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[var(--accent)]">{icon}</span>
        <h3 className="text-[12.5px] font-semibold text-[var(--fg)]">{title}</h3>
        <span className="text-[11px] text-[var(--fg-muted)]">{count}</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function SharedRow({
  href,
  leading,
  title,
  badge,
  sharedAt,
  revoking,
  onRevoke,
}: {
  href: string;
  leading: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  sharedAt: string | null;
  revoking: boolean;
  onRevoke?: () => void;
}) {
  return (
    <div className="group flex items-center gap-3 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--border-strong)] transition-colors px-3 py-2">
      {leading}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 min-w-0 flex-1 text-[var(--fg)] hover:text-[var(--accent)] transition-colors"
        title="Open public link in a new tab"
      >
        <span className="truncate text-[12.5px]">{title}</span>
        <ExternalLink size={11} className="shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
      </a>
      {badge}
      <span className="text-[11px] text-[var(--fg-muted)] shrink-0 whitespace-nowrap">
        {sharedAt ? `shared ${formatRelativeTime(sharedAt)}` : ""}
      </span>
      {onRevoke && (
        <button
          onClick={onRevoke}
          disabled={revoking}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-[var(--red)]/30 hover:bg-[var(--red)]/10 text-[var(--red)] text-[11.5px] font-medium transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-60 shrink-0"
        >
          {revoking ? <Loader2 size={11} className="animate-spin" /> : "Revoke"}
        </button>
      )}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 text-[var(--fg-muted)]">
      <Link2 size={32} className="mb-3 opacity-40" />
      <div className="text-[13px] font-medium mb-1 text-[var(--fg-secondary)]">{title}</div>
      <div className="text-[11.5px] max-w-md">{body}</div>
    </div>
  );
}
